import { useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import type { AppAction } from '@/lib/appStatus';
import { isDestructive } from '@/lib/appStatus';
import type { App } from '@/types/app';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ScaleAppDialog } from '@/components/ScaleAppDialog';
import { RedeployDialog } from '@/components/RedeployDialog';
import { ChangeDomainDialog } from '@/components/apps/ChangeDomainDialog';
import type { DomainSummary, DomainSwitch } from '@/types/domain';

type ConfirmAction = 'stop' | 'start' | 'delete';

interface PendingConfirm {
  action: ConfirmAction;
  app: App;
  open: boolean;
}

const COPY: Record<
  ConfirmAction,
  { title: string; confirmLabel: string; description: (name: string) => string; success: string; failure: string }
> = {
  stop: {
    title: 'Stop app',
    confirmLabel: 'Stop',
    description: (n) => `Stop '${n}'? This stops its containers but keeps the stack.`,
    success: 'App stopped',
    failure: 'Failed to stop app',
  },
  start: {
    title: 'Start app',
    confirmLabel: 'Start',
    description: (n) => `Start '${n}'? This restarts its containers.`,
    success: 'App started',
    failure: 'Failed to start app',
  },
  delete: {
    title: 'Delete app',
    confirmLabel: 'Delete',
    description: (n) =>
      `Delete '${n}'? This permanently removes the app and its stack. This can't be undone.`,
    success: 'App deleted',
    failure: 'Failed to delete app',
  },
};

const busyKey = (appId: number, action: AppAction) => `${appId}:${action}`;

export function useAppActions({
  onChanged,
  onDeleted,
  domains = [],
  onDomainSwitchStarted,
}: {
  onChanged: () => void;
  onDeleted?: () => void;
  /** Targets for Change domain (only offered where they are known). */
  domains?: DomainSummary[];
  /** A domain switch was queued; defaults to onChanged. */
  onDomainSwitchStarted?: (sw: DomainSwitch) => void;
}): { request: (action: AppAction, app: App) => void; isBusy: (appId: number, action?: AppAction) => boolean; dialogs: ReactNode } {
  const [confirm, setConfirm] = useState<PendingConfirm | null>(null);
  const [scaleApp, setScaleApp] = useState<App | null>(null);
  const [scaleOpen, setScaleOpen] = useState(false);
  const [redeployApp, setRedeployApp] = useState<App | null>(null);
  const [redeployOpen, setRedeployOpen] = useState(false);
  const [domainApp, setDomainApp] = useState<App | null>(null);
  const [busy, setBusy] = useState<string[]>([]);
  const inFlight = useRef<Set<string>>(new Set());

  const request = (action: AppAction, app: App) => {
    switch (action) {
      case 'scale':
        setScaleApp(app);
        setScaleOpen(true);
        return;
      case 'redeploy':
        setRedeployApp(app);
        setRedeployOpen(true);
        return;
      case 'change-domain':
        setDomainApp(app);
        return;
      case 'stop':
      case 'start':
      case 'delete':
        setConfirm({ action, app, open: true });
        return;
      default: {
        const _exhaustive: never = action;
        throw new Error(`Unhandled app action: ${String(_exhaustive)}`);
      }
    }
  };

  const isBusy = (appId: number, action?: AppAction) =>
    action
      ? busy.includes(busyKey(appId, action))
      : busy.some((k) => k.startsWith(`${appId}:`));

  const runConfirmed = async () => {
    if (!confirm) return;
    const { action, app } = confirm;
    const key = busyKey(app.id, action);
    if (inFlight.current.has(key)) return;
    inFlight.current.add(key);
    const copy = COPY[action];
    setBusy((prev) => [...prev, key]);
    let ok = false;
    try {
      switch (action) {
        case 'stop':
          await api.apps.stop(app.id);
          break;
        case 'start':
          await api.apps.start(app.id);
          break;
        case 'delete':
          await api.apps.delete(app.id);
          break;
        default: {
          const _exhaustive: never = action;
          throw new Error(`Unhandled confirm action: ${String(_exhaustive)}`);
        }
      }
      toast.success(copy.success);
      ok = true;
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : copy.failure);
    } finally {
      inFlight.current.delete(key);
      setBusy((prev) => prev.filter((k) => k !== key));
      setConfirm((prev) => (prev ? { ...prev, open: false } : prev));
    }
    if (ok && action === 'delete') (onDeleted ?? onChanged)();
    else onChanged(); // state may have changed even on failure (e.g. 409)
  };

  const confirmLoading = confirm ? busy.includes(busyKey(confirm.app.id, confirm.action)) : false;

  const dialogs = (
    <>
      {confirm ? (
        <ConfirmDialog
          open={confirm.open}
          onOpenChange={(open) => setConfirm((prev) => (prev ? { ...prev, open } : prev))}
          title={COPY[confirm.action].title}
          description={COPY[confirm.action].description(confirm.app.display_name || confirm.app.name)}
          confirmLabel={COPY[confirm.action].confirmLabel}
          destructive={isDestructive(confirm.action)}
          loading={confirmLoading}
          onConfirm={runConfirmed}
        />
      ) : null}
      <ScaleAppDialog
        open={scaleOpen}
        onOpenChange={setScaleOpen}
        app={scaleApp}
        onSuccess={onChanged}
      />
      <ChangeDomainDialog
        app={domainApp}
        domains={domains}
        onClose={() => setDomainApp(null)}
        onStarted={(sw) => {
          setDomainApp(null);
          if (onDomainSwitchStarted) onDomainSwitchStarted(sw);
          else onChanged();
        }}
      />
      {redeployApp ? (
        <RedeployDialog
          open={redeployOpen}
          onOpenChange={setRedeployOpen}
          app={redeployApp}
          onSuccess={onChanged}
        />
      ) : null}
    </>
  );

  return { request, isBusy, dialogs };
}
